import { useState } from 'react';
import {
  Loader2,
  RefreshCw,
  AlertTriangle,
  Clock,
  Server,
  ChevronDown,
  Copy,
  Check,
  FileText,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useEncompassTransaction } from '../../hooks/useLosProxy';

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  processing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  canceled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  created: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status?.toLowerCase()] || STATUS_COLORS.created;
  return (
    <span className={clsx('inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-0.5', color)}>
      {status}
    </span>
  );
}

interface Props {
  transactionId: string;
}

export default function EncompassTransactionInspector({ transactionId }: Props) {
  const { data: transaction, isLoading, error, refetch, isFetching } = useEncompassTransaction(transactionId);
  const [showRawJson, setShowRawJson] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(transaction, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!transactionId) {
    return (
      <div className="text-center py-6">
        <Server className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No Encompass transaction ID available for this order</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading transaction details...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">Failed to load Encompass transaction</p>
        <p className="text-xs text-gray-400 mt-1">{(error as Error).message}</p>
        <button onClick={() => refetch()} className="btn-secondary text-xs mt-3">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-indigo-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Encompass Transaction</h4>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="btn-icon p-1.5"
          title="Refresh"
        >
          <RefreshCw className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
        </button>
      </div>

      {/* Transaction Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Transaction ID</p>
          <p className="text-xs text-gray-800 dark:text-gray-200 font-mono mt-0.5 truncate" title={transactionId}>{transactionId}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Status</p>
          <div className="mt-0.5">
            <StatusBadge status={transaction?.status || 'Unknown'} />
          </div>
        </div>
        {transaction?.response?.status && (
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Response Status</p>
            <div className="mt-0.5">
              <StatusBadge status={transaction.response.status} />
            </div>
          </div>
        )}
        {transaction?.response?.partnerStatus && (
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Partner Status</p>
            <p className="text-xs text-gray-600 dark:text-gray-400 font-mono mt-0.5 truncate">{transaction.response.partnerStatus}</p>
          </div>
        )}
        {transaction?.clientId && (
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Client ID</p>
            <p className="text-xs text-gray-600 dark:text-gray-400 font-mono mt-0.5 truncate">{transaction.clientId}</p>
          </div>
        )}
      </div>

      {/* Response Errors */}
      {transaction?.response?.errors && transaction.response.errors.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Errors</p>
          {transaction.response.errors.map((err, i) => (
            <div key={i} className="p-2.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/20">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                <div>
                  {err.code && <span className="text-[10px] text-red-500 font-mono">{err.code}</span>}
                  <p className="text-xs text-red-700 dark:text-red-300">{err.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resources */}
      {transaction?.response?.resources && transaction.response.resources.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Resources ({transaction.response.resources.length})</p>
          <div className="space-y-1">
            {transaction.response.resources.map((res, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50">
                <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-700 dark:text-gray-300 truncate">{res.name}</p>
                  {res.mimeType && <p className="text-[10px] text-gray-400">{res.mimeType}</p>}
                </div>
                {res.size && <span className="text-[10px] text-gray-400 shrink-0">{(res.size / 1024).toFixed(1)}KB</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Events */}
      {transaction?.events && transaction.events.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">EPC Events ({transaction.events.length})</p>
          <div className="space-y-1">
            {transaction.events.map((evt, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50">
                <Clock className="h-3 w-3 text-gray-400 shrink-0" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{evt.type}</span>
                <span className="text-[10px] text-gray-400 ml-auto shrink-0">
                  {new Date(evt.date).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw JSON toggle */}
      <div>
        <button
          onClick={() => setShowRawJson(!showRawJson)}
          className="text-[11px] text-blue-500 hover:text-blue-400 flex items-center gap-1"
        >
          <ChevronDown className={clsx('h-3 w-3 transition-transform', showRawJson && 'rotate-180')} />
          {showRawJson ? 'Hide' : 'Show'} raw response
        </button>
        {showRawJson && (
          <div className="mt-2 relative">
            <button onClick={handleCopy} className="absolute top-2 right-2 btn-icon p-1" title="Copy JSON">
              {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
            </button>
            <pre className="text-[11px] text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50 rounded-lg p-3 overflow-auto max-h-72 border border-gray-200 dark:border-gray-700/50 font-mono">
              {JSON.stringify(transaction, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
